import { find, isArray, toNumber } from 'lodash-es';
import axios from 'axios';
import FifoQueue from 'Assets/js/components/fifoQueue';
import events from 'Assets/js/components/events';
import rateLimit from 'axios-rate-limit';
import getCookie from 'Assets/js/components/cookie';
import globalTheme from 'Assets/js/components/globalTheme';
import b2bCartHelpers from 'Assets/js/components/b2bCartHelpers';
import cartContent from 'Templates/content/cart.json';
import store from 'Assets/js/Vuex/Store/store';
import markets from 'Assets/js/components/markets';
import marketExclusiveService from 'Assets/js/components/marketExclusiveService';

// @todo This is set in different modules: isn't it better to do it once, e.g. in main.js?
const http = rateLimit(axios.create(), {
  maxRequests: 12,
  perMilliseconds: 1000,
  maxRPS: 12
});

let cartLines;

const toastMessages = {
  success: '✅ The product has been added to your cart.',
  error:
    '❌ <strong>Error:</strong> the product couldn’t be added. Please try again or <a href="/contact-us/">contact us</a>.',
  purchaseLimit:
    '⚠️ <strong>Limit reached:</strong> you’ve hit the maximum quantity allowed for this product.'
};

const debugEnabled =
  window.global_theme_debug && window.global_theme_debug.cart;

function triggerError(message) {
  window.dispatchEvent(
    new CustomEvent('error-toggled', {
      detail: {
        subtitle: 'The shop is not available',
        title: 'Please retry in a few seconds',
        content: `${message} (This event has been reported)`
      }
    })
  );
}

const triggerErrorLog = (error) => {
  axios.post('/wp-json/teams/v1/send', {
    error: { 'service: ': 'cartService', error }
  });
};

function validateResponse(response) {
  if (response.status !== 200) {
    triggerError(`Network error: ${response.statusText}`);
    triggerErrorLog({
      message: `Network error: ${response.statusText}`,
      page: window.location.href
    });
  }

  if (response.data.errors && response.data.errors.length > 0) {
    triggerErrorLog({
      message: 'errors',
      errors: response.data.errors,
      page: window.location.href
    });
  }
  if (response.data.data) {
    const type = Object.keys(response.data.data)[0];
    if (
      response.data.data[type].userErrors &&
      response.data.data[type].userErrors.length > 0 &&
      response.data.data[type].userErrors[0].message !== 'must not be blank'
    ) {
      triggerErrorLog({
        message: 'user errors',
        errors: response.data.data[type].userErrors,
        page: window.location.href
      });
      triggerError(response.data.data[type].userErrors[0].message);
    }
  }
}

function getActiveRecipients(lines) {
  const people = [];
  // @todo Replace map with forEach
  // eslint-disable-next-line array-callback-return
  lines.edges.map((product) => {
    const valid = product.node.attributes.find(
      (attribute) => attribute.key === 'recipient'
    );
    if (valid) {
      const validValue = product.node.attributes.find(
        (attribute) => attribute.key === 'recipient'
      ).value;
      if (!people.includes(validValue)) {
        people.push(validValue);
      }
    }
  });
  return people;
}

function getRecepientLocations() {
  const b2bHelper = b2bCartHelpers.getInstance();
  b2bHelper.getMarketsFromRecepients();
  return store.getters.b2bCartLocations;
}

function formatB2bCart(cart) {
  const activeRecepients = getActiveRecipients(cart.lines);
  if (activeRecepients) {
    store.dispatch('setB2bAllRecipients', activeRecepients);
    getRecepientLocations(activeRecepients);
  }
  return cart;
}

/**
 *
 * @return {*}
 * @constructor
 */
function CartServiceConstructor() {
  axios.interceptors.response.use((response) => {
    validateResponse(response);
    // eslint-disable-next-line no-console
    return response;
  });

  let getCart;
  let checkCountryCode;
  let updateCountryCode;
  let updateCountryCodeWith;
  let getNewCart;

  const queue = new FifoQueue();

  const uniqueCartId = `cart_${Math.round(Math.random() * 316315531441)}`;

  if (!globalTheme) {
    // eslint-disable-next-line no-console
    console.error('globalTheme is undefined');
  }

  const getShopifySettings = () => {
    const fromGlobalTheme =
      typeof globalTheme?.shopify === 'function'
        ? globalTheme.shopify()
        : null;

    if (fromGlobalTheme && Object.keys(fromGlobalTheme).length) {
      return fromGlobalTheme;
    }

    if (typeof window !== 'undefined') {
      const fromWindow = window.global_theme_object?.shopify;
      if (fromWindow && Object.keys(fromWindow).length) {
        return fromWindow;
      }
    }

    return {};
  };

  const getShopifyRequestUrl = () => {
    const settings = getShopifySettings();
    if (!settings?.url || !settings?.api_version) {
      return null;
    }

    return `https://${settings.url}/api/${settings.api_version}/graphql.json`;
  };

  const getStorefrontAccessToken = () =>
    getShopifySettings()?.store_front_access_token || null;

  const getGiftBagProductId = () =>
    getShopifySettings()?.gift_bag_product_id || null;

  if (debugEnabled) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG] Shopify settings', getShopifySettings());
  }

  let cartId = globalTheme.cartId();

  let cartInitialised = false;

  const initCart = (closure) => {
    if (cartInitialised) {
      if (typeof closure === 'function') {
        closure();
      }
      return;
    }

    const initCartCallback = () => {
      checkCountryCode();

      if (typeof closure === 'function') {
        closure();
      }

      cartInitialised = true;
    };

    if (!cartId) {
      let tempCartId = getCookie('STYXKEY_user_cart');
      if (tempCartId) {
        tempCartId = decodeURIComponent(tempCartId);
        cartId = tempCartId;
      }
    }

    if (!cartId) {
      getNewCart(() => {
        initCartCallback();
      }, true);
    } else {
      initCartCallback();
    }
  };

  /**
   * Normalise product ID, adding gid:// prefix.
   * This will handle correctly a numeric productId.
   *
   * @param productId
   * @returns {string}
   */
  const normaliseProductId = (productId) => {
    if (productId && !`${productId}`.includes('gid://shopify/Product/')) {
      productId = `gid://shopify/Product/${productId}`;
    }
    return productId;
  };

  let currentCart = {};
  const triggeredPromos = new Set();

  let countryCode = globalTheme.localCode();

  /**
   * Check if B2C cart / B2B market carts contain at least one item
   * It works differently for B2C and B2B
   * @return {boolean}
   */
  const hasCartItems = () =>
    store.getters?.isB2B
      ? !!Object.keys(store.getters.b2bGlobalProducts).length
      : !!currentCart?.lines?.edges.length;

  const checkForValidOffers = (productId) => {
    if (!globalTheme.featureEnabled().promo_popups) {
      return;
    }

    const promotions = globalTheme.promotions();
    const promotionDetails = globalTheme.promotionDetails();

    const dispatchPromo = (promoId) => {
      const cookieThatDisablesThis = getCookie(`STYXKEY_no_promos_pls_${promoId}`);
      if (cookieThatDisablesThis) {
        return;
      }
      if (triggeredPromos.has(promoId)) {
        return;
      }
      triggeredPromos.add(promoId);
      window.dispatchEvent(
        new CustomEvent('promo-toggled', {
          detail: {
            promoId,
            promoDetails: promotionDetails[promoId]
          }
        })
      );
    };

    // Trigger by matching product
    Object.entries(promotions).forEach((value) => {
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.log(
          '[DEBUG] cartService.checkForValidOffers, looking at this promo: ',
          value
        );
      }
      const list = value[1];
      const matchesAll = Array.isArray(list) && list.indexOf('all') !== -1;
      const hasProductId = typeof productId === 'string'
        ? productId.trim() !== ''
        : Boolean(productId);
      if (matchesAll || (hasProductId && `${list}`.includes(productId))) {
        const thisId = value[0];
        const t = (promotionDetails[thisId] && promotionDetails[thisId].trigger_type) || 'product_add';
        if (t === 'product_add' || t === 'both') {
          dispatchPromo(thisId);
        }
      }
    });

    // Trigger by cart subtotal threshold (if configured on the promo)
    try {
      const subtotal = parseFloat(
        (currentCart && currentCart.cost && currentCart.cost.subtotalAmount && currentCart.cost.subtotalAmount.amount)
          ? currentCart.cost.subtotalAmount.amount
          : '0'
      );

      // Determine buyer market country code (e.g. 'IE', 'GB')
      const buyerCountry = currentCart?.buyerIdentity?.countryCode || globalTheme.localCode();

      // Currency conversion support: thresholds are authored in IE/EUR and must be
      // compared against the cart subtotal in the buyer's currency.
      // We compute a lightweight FX factor per country by comparing a reference
      // product price in the target market vs IE market and cache it.

      const fxCache = checkForValidOffers.__fxCache || (checkForValidOffers.__fxCache = {});

      const getRefPrice = async (countryCode) => {
        try {
          const shopifySettings = globalTheme.shopify();
          // Prefer a stable reference product (gift bag). Fallback to first cart product.
          const productId = shopifySettings?.gift_bag_product_id || currentCart?.lines?.edges?.[0]?.node?.merchandise?.product?.id;
          if (!productId) return null;

          const data = JSON.stringify({
            query: `query product($countryCode: CountryCode!, $productId: ID!)
              @inContext(country: $countryCode) {
                product(id: $productId) {
                  price: variants(first: 1) { edges { node { priceV2 { amount currencyCode } } } }
                }
              }`,
            variables: { countryCode, productId }
          });
          const config = getHttpConfig(data);
          const resp = await http(config);
          const edges = resp?.data?.data?.product?.price?.edges || [];
          const amt = edges[0]?.node?.priceV2?.amount;
          return amt != null ? parseFloat(amt) : null;
        } catch (err) {
          return null;
        }
      };

      const getFxToLocal = async (targetCountry) => {
        if (!targetCountry || String(targetCountry).toUpperCase() === 'IE') return 1;
        if (fxCache[targetCountry]) return fxCache[targetCountry];
        // Fetch reference prices in IE and target market
        const [iePrice, targetPrice] = await Promise.all([
          getRefPrice('IE'),
          getRefPrice(targetCountry)
        ]);
        let fx = 1;
        if (iePrice && targetPrice && iePrice > 0) {
          fx = targetPrice / iePrice;
        }
        fxCache[targetCountry] = fx;
        return fx;
      };

      const evaluateThresholds = async () => {
        // Pre-compute FX once per evaluation
        const fx = await getFxToLocal(buyerCountry);

        Object.entries(promotionDetails || {}).forEach(([id, details]) => {
          const baseMin = parseFloat((details && details.min_cart_value) ? details.min_cart_value : 0);
          const t = (details && details.trigger_type) || 'product_add';
          if (isNaN(baseMin) || baseMin <= 0 || !(t === 'cart_value' || t === 'both')) return;

          // Convert base (IE/EUR) threshold into buyer currency using FX
          const localMin = baseMin * (Number.isFinite(fx) && fx > 0 ? fx : 1);

          if (subtotal >= localMin) {
            if (debugEnabled) {
              // eslint-disable-next-line no-console
              console.log('[DEBUG] cartService.checkForValidOffers threshold match', { id, baseMin, localMin, subtotal, buyerCountry });
            }
            dispatchPromo(id);
          }
        });
      };

      // Fire and forget; promo dispatching happens when conversion is ready
      // (sync if no conversion is needed)
      Promise.resolve(evaluateThresholds());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Promo subtotal check failed', e);
    }
  };

  const togglePersonalisationModel = (
    variantId,
    productId,
    title,
    img,
    limit,
    engraving = null,
    b2bCart = {}
  ) => {
    window.dispatchEvent(
      new CustomEvent('personalisation-toggled', {
        detail: {
          variant: variantId,
          productId,
          title,
          img,
          limit,
          engraving,
          b2bCart
        }
      })
    );
  };

  const toggleVariantPersonalisationModel = (
    variantId,
    productId,
    title,
    img,
    limit,
    productVariants = [],
    engraving = null,
    b2bCart = {}
  ) => {
    window.dispatchEvent(
      new CustomEvent('personalisation-toggled', {
        detail: {
          variant: variantId,
          productId,
          title,
          img,
          limit,
          productVariants,
          engraving,
          b2bCart
        }
      })
    );
  };

  /**
   * Update cart items counter
   * @param {Object|undefined} cartData
   */
  const updateCartItemsCounter = (cartData = undefined) => {
    const callback = (data) => {
      const $cartItemElements = document.querySelectorAll(
        '.global-search-bar-basket .global-search-bar--items-count'
      );

      $cartItemElements.forEach(($cartItemCount) => {
        let totalQuantity = '';
        if (data && data.lines && data.lines.edges.length) {
          totalQuantity = 0;
          data.lines.edges.forEach((item) => {
            let isEngraving = false;
            item.node.attributes.forEach((attribute) => {
              if (
                attribute.key === 'is_engrave_product' &&
                attribute.value === 'true'
              ) {
                isEngraving = true;
              }
            });
            if (isEngraving === false) {
              totalQuantity += item.node.quantity;
            }
            if (store.getters.isB2B) {
              const quantity = Object.values(store.getters.b2bGlobalProducts);
              totalQuantity = quantity.length > 0 ? quantity.length : '-';
            }
          });
        }

        $cartItemCount.innerText = totalQuantity;
        if (totalQuantity) {
          $cartItemCount.classList.add('global-search-bar--items-count-active');
        } else {
          $cartItemCount.classList.remove(
            'global-search-bar--items-count-active'
          );
        }
      });
    };

    if (cartData) {
      callback(cartData);
    } else {
      getCart();
    }
  };

  /**
   * Fetch all cartLines by product
   * @param {object} product
   * @return array
   */
  const findCartItemsByProduct = (product) => {
    if (
      !product ||
      typeof product !== 'object' ||
      typeof cartLines !== 'object' ||
      cartLines.length === 0
    ) {
      return [];
    }

    return cartLines.edges.filter(
      (cartLine) =>
        cartLine.node.merchandise.product.id ===
        (product.shopify_id ?? product.id)
    );
  };

  const findCartQuantityTotalByProduct = (product) => {
    let quantityInCart = 0;
    findCartItemsByProduct(product).forEach((cartLine) => {
      quantityInCart += cartLine.node.quantity;
    });
    return quantityInCart;
  };

  /**
   * Normalise quantity, checking if a product has purchase limitations
   *
   * @param variantId
   * @param quantity
   * @returns {number}
   */
  const normaliseQuantity = async (variantId, quantity) => {
    quantity = toNumber(quantity);

    const product = await globalTheme.productByVariantId(variantId);
    if (product && product.meta) {
      const purchaseLimit = toNumber(product.meta.purchase_limit) || 0;
      if (purchaseLimit) {
        const newQuantity = Math.min(quantity, purchaseLimit);
        const quantityInCart = findCartQuantityTotalByProduct(product);

        if (quantityInCart) {
          const newMaxQuantity = purchaseLimit - quantityInCart;
          return newMaxQuantity >= 0 ? newMaxQuantity : 0;
        }

        return newQuantity;
      }
    }
    return quantity;
  };

  /**
   * Get config to be used by Axios requests
   *
   * @param data
   * @returns {*}
   */
  const getHttpConfig = (data) => {
    const requestUrl = getShopifyRequestUrl();
    const storefrontAccessToken = getStorefrontAccessToken();

    if (!requestUrl || !storefrontAccessToken) {
      triggerError('Missing Shopify configuration');
      throw new Error('Missing Shopify configuration');
    }

    return {
      method: 'post',
      url: requestUrl,
      headers: {
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };
  };

  /**
   * Add to cart
   * @param variantId
   * @param {String|Number} quantity
   * @param productId
   * @param {Function|undefined} callback
   */
  const addToCart = async (
    variantId,
    quantity = 1,
    productId,
    callback = undefined
  ) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.addToCart', {
        cartInitialised: !!cartId,
        variantId,
        quantity,
        productId
      });
    }

    if (quantity === 0) {
      if (debugEnabled) {
        // @todo Fix this: addToCart() with 0 quantity is called e.g. from removeBagCart

        // eslint-disable-next-line no-console
        console.error('cartService.addToCart failed: invalid quantity', {
          variantId,
          quantity,
          productId
        });
      }

      return null;
    }

    productId = normaliseProductId(productId);

    if (!cartId) {
      return new Promise((resolve, reject) => {
        initCart(() => {
          addToCart(variantId, quantity, productId, callback)
            .then(resolve)
            .catch(reject);
        });
      });
    }

    if (!variantId) {
      // eslint-disable-next-line no-console
      triggerError(
        'Sorry, we are unable to add this product to your basket. It is likely out of stock, or there has been a connection issue. Please try again later.'
      );
      // eslint-disable-next-line no-console
      console.error('cartService.addToCart failed: invalid variant ID', {
        variantId
      });
      try {
        window.dispatchEvent(
          new CustomEvent('toast', {
            detail: {
              message: toastMessages.error,
              type: 'error',
              timeout: 6000
            }
          })
        );
      } catch (e) {}
      if (typeof callback === 'function') {
        callback({ status: 200 });
      }
      return { status: 200 };
    }

    [quantity] = await Promise.all([normaliseQuantity(variantId, quantity)]);
    if (!quantity) {
      // eslint-disable-next-line no-console
      console.warn(
        'cartService.addToCart failed: invalid quantity (product with limitations)',
        {
          variantId,
          quantity
        }
      );
      try {
        window.dispatchEvent(
          new CustomEvent('toast', {
            detail: {
              message: toastMessages.purchaseLimit,
              type: 'warning',
              timeout: 6000
            }
          })
        );
      } catch (e) {}
      if (typeof callback === 'function') {
        callback({ status: 200 });
      }
      return { status: 200 };
    }

    const mutation = () =>
      new Promise((resolve) => {
        const data = JSON.stringify({
          query: `mutation cartLinesAdd($lines: [CartLineInput!]!, $cartId: ID!) {
                cartLinesAdd( lines: $lines, cartId: $cartId ) {
                  cart {
                    id
                    lines(first: 50, reverse: true) {
                      edges {
                        node{
                          id
                        }
                      }
                    }
                  }
                  warnings {
                    code
                    message
                    target
                  }
                  userErrors {
                    field
                    message
                  }
                }
              }`,
          variables: {
            cartId,
            lines: {
              merchandiseId: variantId,
              quantity: toNumber(quantity)
            }
          }
        });

        const config = getHttpConfig(data);

        queue.push((queueCallback) => {
          http(config)
            .then((response) => {
              if (debugEnabled) {
                // eslint-disable-next-line no-console
                console.log('[DEBUG] cartService.addToCart response', {
                  variantId,
                  quantity,
                  productId,
                  responseData: response.data.data
                });
              }

              let errorToastShown = false;

              if (
                response.data.data.cartLinesAdd.warnings.some(
                  (w) => w.code === 'MERCHANDISE_NOT_ENOUGH_STOCK'
                )
              ) {
                window.dispatchEvent(
                  new CustomEvent('merchandise_not_enough_stock_triggered', {
                    detail: { quantity }
                  })
                );
                if (!errorToastShown) {
                  window.dispatchEvent(
                    new CustomEvent('toast', {
                      detail: {
                        message: toastMessages.error,
                        type: 'error',
                        timeout: 6000
                      }
                    })
                  );
                  errorToastShown = true;
                }
              }

              if (response.data.data.cartLinesAdd.userErrors.length > 0) {
                triggerErrorLog(response.data.data.cartLinesAdd.userErrors);
                if (!errorToastShown) {
                  window.dispatchEvent(
                    new CustomEvent('toast', {
                      detail: {
                        message: toastMessages.error,
                        type: 'error',
                        timeout: 6000
                      }
                    })
                  );
                  errorToastShown = true;
                }
              }

              if (response.data.errors) {
                triggerErrorLog(response.data.errors);
                if (!errorToastShown) {
                  window.dispatchEvent(
                    new CustomEvent('toast', {
                      detail: {
                        message: toastMessages.error,
                        type: 'error',
                        timeout: 6000
                      }
                    })
                  );
                  errorToastShown = true;
                }
              }

              events.addToCart(variantId, quantity, { hasCart: hasCartItems() });

              if (!errorToastShown) {
                window.dispatchEvent(
                  new CustomEvent('toast', {
                    detail: { message: toastMessages.success, type: 'success' }
                  })
                );
              }

              if (store.getters?.isB2B && quantity !== 0) {
                globalTheme.productByVariantId(variantId).then((product) => {
                  store.dispatch('addProductIdToGlobalCart', {
                    productId,
                    quantity,
                    merchandise: product,
                    variantId,
                    lineItem:
                      response.data.data.cartLinesAdd.cart.lines.edges[0].node.id
                  });
                });
              }

              if (typeof callback === 'function') {
                callback(response);
              }

              queueCallback();

              // @todo Retrieve product from variant ID
              checkForValidOffers(productId);
              resolve(response);
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('cartService.addToCart failed', error);
              triggerErrorLog(error);
              if (typeof callback === 'function') {
                callback({ status: 500, error });
              }
              queueCallback();
              resolve(null);
            });
        });
      });

    let marketExclusives = [];
    if (store.getters?.isB2B && globalTheme.marketExclusive().b2b) {
      marketExclusives = await marketExclusiveService.getAvailableMarkets(
        productId
      );
    }

    if (marketExclusives.length) {
      if (marketExclusives.indexOf(countryCode) === -1) {
        const newMarket =
          marketExclusives.indexOf('IE') !== -1 ? 'IE' : marketExclusives[0];
        return new Promise((resolve) => {
          updateCountryCodeWith(newMarket, async () => {
            const response = await mutation();
            resolve(response);
          });
        });
      }
      return mutation();
    }
    return mutation();
  };

  /**
   * Add to cart with personalisation
   * @param variantId
   * @param message
   * @param productId
   * @param {Function|undefined} callback
   */
  const addToCartWithPersonalisation = (
    variantId,
    message,
    productId,
    callback = undefined
  ) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.addToCartWithPersonalisation', {
        variantId,
        message,
        productId
      });
    }

    if (!variantId) {
      // eslint-disable-next-line no-console
      triggerErrorLog(
        'cartService.addToCartWithPersonalisation failed: invalid variant ID'
      );
      // eslint-disable-next-line no-console
      console.error(
        'cartService.addToCartWithPersonalisation failed: invalid variant ID',
        {
          variantId,
          message,
          productId
        }
      );
      return;
    }

    if (!cartId) {
      initCart(() => {
        addToCartWithPersonalisation(variantId, message, productId, callback);
      });
      return;
    }

    productId = normaliseProductId(productId);

    const data = JSON.stringify({
      query: `mutation cartLinesAdd($lines: [CartLineInput!]!, $cartId: ID!) {
                cartLinesAdd( lines: $lines, cartId: $cartId ) {
                  cart {
                    id
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        lines: {
          merchandiseId: variantId,
          quantity: 1,
          attributes: {
            key: cartContent.lineItemsAttributes.personalisation,
            value: message
          }
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        events.addToCart(variantId, 1, {
          personalised: true,
          hasCart: hasCartItems()
        });

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }

        queueCallback();
        checkForValidOffers(productId);
      });
    });
  };

  const addToCartWithBottledBy = (
    variantId,
    message,
    productId,
    callback = undefined,
    withFor = true
  ) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.addToCartWithBottledBy', {
        variantId,
        message,
        productId,
        withFor
      });
    }

    if (!variantId) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.addToCartWithBottledBy failed: invalid variant ID',
        {
          variantId,
          message,
          productId,
          withFor
        }
      );
      return;
    }

    if (!cartId) {
      initCart(() => {
        addToCartWithBottledBy(
          variantId,
          message,
          productId,
          callback,
          withFor
        );
      });
      return;
    }

    productId = normaliseProductId(productId);

    // Prepare attributes
    const attributes = [
      {
        key: 'Bottled by',
        value: message.bottledBy
      }
    ];
    if (withFor) {
      attributes.push({
        key: 'For',
        value: message.for
      });
    }

    const data = JSON.stringify({
      query: `mutation cartLinesAdd($lines: [CartLineInput!]!, $cartId: ID!) {
                cartLinesAdd( lines: $lines, cartId: $cartId ) {
                  cart {
                    id
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        lines: {
          merchandiseId: variantId,
          quantity: 1,
          attributes
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        events.addToCart(variantId, 1, {
          personalised: true,
          hasCart: hasCartItems()
        });

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }

        queueCallback();
        checkForValidOffers(productId);
      });
    });
  };

  const addToCartWithBottledByOnly = (
    variantId,
    message,
    productId,
    callback = undefined
  ) => {
    addToCartWithBottledBy(variantId, message, productId, callback, false);
  };

  /**
   * Update cart item
   *
   * @param variantId
   * @param productId
   * @param {String|Number} quantity
   * @param {Function|undefined} callback
   */
  const updateCart = (variantId, productId, quantity = 1, callback) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.updateCart', {
        variantId,
        productId,
        quantity
      });
    }

    if (!productId) {
      // eslint-disable-next-line no-console
      console.error('cartService.productId failed: invalid product ID', {
        productId
      });
      return;
    }

    if (!cartId) {
      initCart(() => {
        updateCart(variantId, productId, quantity);
      });
      return;
    }

    const variant = btoa(variantId);
    const query = `
      mutation cartLinesAdd(\\$lines: [CartLineInput!]!, \\$cartId: ID!) {
          cartLinesAdd( lines: \\$lines, cartId: \\$cartId ) {
            cart {
              id
            }
            userErrors {
              code
              field
              message
            }
          }
        }`;

    queue.push((queueCallback) => {
      http
        .post({
          url,
          headers: {
            'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query,
            variables: {
              cartId,
              lines: {
                merchandiseId: variant,
                quantity: parseInt(quantity, 10)
              }
            }
          })
        })
        .then((response) => {
          if (response.data.errors) {
            triggerErrorLog(response.data.errors);
          }

          localStorage.setItem('cart', JSON.stringify(response.data.data));
          // Refresh cart, then check threshold-based promos
          getCart(() => { try { checkForValidOffers(''); } catch(e) {} });
        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  /**
   * Update quantity of a line item of the cart
   * @param cartLineId
   * @param variantId
   * @param quantity
   * @param {Function|undefined} callback
   */
  const updateQuantity = (cartLineId, variantId, quantity, callback) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.updateQuantity', {
        cartLineId,
        quantity
      });
    }

    if (store.getters?.isB2B && quantity !== 0) {
      store.dispatch('updateProductInGlobalCart', {
        variantId,
        quantity
      });
    }

    if (!cartLineId) {
      // eslint-disable-next-line no-console
      console.error('cartService.updateQuantity failed: invalid cart line ID', {
        cartLineId,
        variantId,
        quantity
      });
      return;
    }

    if (!variantId) {
      // eslint-disable-next-line no-console
      console.warn(
        'cartService.updateQuantity not working properly: invalid variant ID',
        {
          cartLineId,
          variantId,
          quantity
        }
      );
    }

    if (!cartId) {
      initCart(() => {
        updateQuantity(cartLineId, variantId, quantity, callback);
      });
    }

    const data = JSON.stringify({
      query: `mutation cartLinesUpdate($cartId: ID! $lines: [CartLineUpdateInput!]!, ) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
              }
              userErrors {
                code
                field
                message
              }
            }
          }`,
      variables: {
        cartId,
        lines: {
          id: cartLineId,
          quantity: parseInt(quantity, 10)
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then(async (response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (quantity === 0 && variantId) {
          events.removeFromCart(variantId, quantity);
        }

        if (store.getters.isB2B) {
          await store.dispatch('removeRecipientsProduct', cartLineId);
        }

        // After quantity update, refresh cart so subtotal is current, then check promos
        getCart(() => { try { checkForValidOffers(''); } catch(e) {} });

        if (typeof callback === 'function') {
          // updateCountryCode(); Not sure why this is here also.
          callback(response);
        }
        queueCallback();
      });
    });
  };

  /**
   * Remove from cart
   *
   * @param cartLineId
   *
   * @deprecated This is never used. Remove it and use updateQuantity()
   *             This method won't trigger any GTM events
   */
  const removeFromCart = (cartLineId) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.removeFromCart', {
        cartLineId
      });
    }

    if (!cartLineId) {
      // eslint-disable-next-line no-console
      console.error('cartService.removeFromCart failed: invalid variant ID', {
        cartLineId
      });
      return;
    }

    if (!cartId) {
      initCart(() => {
        removeFromCart(cartLineId);
      });
      return;
    }

    const data = JSON.stringify({
      query: `mutation cartLinesRemove($cartId: ID! $lineIds: [ID!]!, ) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
            }
            userErrors {
              code
              field
              message
            }
          }
        }`,
      variables: {
        cartId,
        lineIds: {
          id: cartLineId
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then(() => {
        queueCallback();
      });
    });
  };

  getNewCart = (callback, silent) => {
    const data = JSON.stringify({
      query: `mutation cartCreate($input: CartInput) {
              cartCreate(input: $input) {
                cart {
                  id
                }
                userErrors {
                  field
                  message
                }
              }
            }`,
      variables: {
        input: {
          buyerIdentity: {
            countryCode
          }
        }
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (response.status === 200) {
        cartId = response.data.data.cartCreate.cart.id;
        const encodedCartId = encodeURIComponent(
          response.data.data.cartCreate.cart.id
        );
        document.cookie = `STYXKEY_user_cart=${encodedCartId};-1; path=/`;

        if (silent) {
          if (typeof callback === 'function') {
            callback();
          }
        } else {
          window.location.reload();
        }
      }
    });
  };

  /**
   * Get cart. It will refresh automatically cart item counter.
   * @param {Function|undefined} callback
   * @param {Boolean} isB2b
   */
  getCart = (callback = undefined, isB2b = false) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.getCart', {
        cartId,
        isB2b
      });
    }

    if (!cartId) {
      initCart(() => {
        getCart(callback, isB2b);
      });
      return;
    }

    const data = JSON.stringify({
      query: `query cart($cartId: ID!, $countryCode: CountryCode!)
                @inContext(country: $countryCode) {
                  cart( id: $cartId ) {
                    id
                    createdAt
                    updatedAt
                    checkoutUrl
                    buyerIdentity{
                      countryCode
                    }
                    attributes {
                      key
                      value
                    }
                    discountCodes {
                      code
                      applicable
                    }
                    lines(first: 25, reverse: true) {
                      edges {
                        node {
                          id
                          attributes{
                            key
                            value
                          }
                          quantity

                          discountAllocations {
                            discountedAmount{
                              amount
                              currencyCode
                            }
                          }
                          cost{
                            subtotalAmount{
                              amount
                            }
                            totalAmount{
                              amount
                            }
                          }
                          merchandise {
                            ... on ProductVariant {
                              id
                              title
                              taxable
                              thumbnail: image {
                                url(transform: { maxHeight: 120, maxWidth: 120 })
                              }
                              product {
                                id
                                tags
                                availableForSale
                                totalInventory
                                variants(first: 6) {
                                  edges {
                                    node {
                                      id
                                    }
                                  }
                                }
                                title
                                images(first: 1) {
                                  edges {
                                    node {
                                      id
                                      src
                                      altText
                                    }
                                  }
                                }
                                requires_personalisation: metafield(namespace: "custom", key: "requires_personalisation") {
                                  value
                                }
                                personalisation_limit: metafield(namespace: "my_fields", key: "personalisation_limit") {
                                  value
                                }
                                personalisation_product: metafield(namespace: "personalisation", key: "product") {
                                  value
                                }
                                bottled_by_for: metafield(namespace: "custom", key: "bottled_by_for") {
                                  value
                                }
                                bottled_by_only: metafield(namespace: "custom", key: "bottled_by_only") {
                                  value
                                }
                              }
                            }
                          }
                          quantity
                        }
                      }
                    }
                    attributes {
                      key
                      value
                    }
                    deliveryCost: deliveryGroups(first: 3){
                      edges{
                        node{
                          selectedDeliveryOption{
                            estimatedCost{
                              amount
                              currencyCode
                            }
                          }
                        }
                      }
                    }
                    cost {
                      totalAmount {
                        amount
                        currencyCode
                      }
                      subtotalAmount {
                        amount
                        currencyCode
                      }
                      totalTaxAmount {
                        amount
                        currencyCode
                      }
                      totalDutyAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                }`,
      variables: {
        cartId,
        countryCode
      }
    });

    const config = getHttpConfig(data);

    let isUpdatingCountryCode = false;

    http(config)
      .then((response) => {
        const responseCart = response?.data?.data?.cart || null;

        if (response?.data?.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (!responseCart) {
          getNewCart(() => getCart(callback, isB2b));
          return;
        }

        const cart = responseCart;

        const buyerCountryCode = cart?.buyerIdentity?.countryCode;

        if (buyerCountryCode !== countryCode) {
          if (!isUpdatingCountryCode) {
            isUpdatingCountryCode = true;

            updateCountryCode(() => {
              isUpdatingCountryCode = false;
              getCart(callback, isB2b);
            });
          }
          return;
        }

        currentCart = cart;
        // Always evaluate threshold-based promos when cart is refreshed
        try { checkForValidOffers(''); } catch(e) {}

        if (typeof callback === 'function') {
          if (isB2b) {
            callback(formatB2bCart(cart));
          } else {
            callback(cart);
          }
        }

        cartLines = cart?.lines;
        window.dispatchEvent(
          new CustomEvent('cartService:cartFetched', { detail: cart })
        );
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching cart:', error);
      });
  };

  checkCountryCode = () => {
    const data = JSON.stringify({
      query: `
        query cart($cartId: ID!, $countryCode: CountryCode!)
          @inContext(country: $countryCode) {
            cart( id: $cartId ) {
              buyerIdentity{
                countryCode
              }
            }
          }`,
      variables: {
        cartId,
        countryCode
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      const buyerCountryCode =
        response?.data?.data?.cart?.buyerIdentity?.countryCode;

      if (!buyerCountryCode) {
        getNewCart();
        return;
      }

      if (buyerCountryCode === countryCode) {
        updateCountryCode();
      }
    });
  };

  /**
   * Grab gift bag
   * @param {Function|undefined} callback
   *
   * @deprecated
   */
  const grabGiftBag = (callback = undefined) => {
    const data = JSON.stringify({
      query: `
        query product($countryCode: CountryCode!, $productId: ID!)
          @inContext(country: $countryCode) {
            product( id: $productId ) {
              id
              title
              availableForSale
              price: variants(first: 1) {
                edges {
                  node {
                    id
                    priceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
      `,
      variables: {
        countryCode,
        productId: getGiftBagProductId()
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        callback(
          response.data && response.data.data && response.data.data.product
        );
      }
    });
  };

  /**
   * Add user to cart
   * @param userToken
   * @param {Function|undefined} callback
   */
  const addUserToCart = (userToken, callback = undefined) => {
    if (!cartId) {
      return;
    }
    if (!userToken) {
      return;
    }

    const data = JSON.stringify({
      query: `mutation cartBuyerIdentityUpdate($cartId: ID! $buyerIdentity: CartBuyerIdentityInput!) {
                cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
                  cart {
                    id
                    buyerIdentity {
                      countryCode
                    }
                  }
                  userErrors {
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        buyerIdentity: {
          customerAccessToken: userToken
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response.data.data.cartBuyerIdentityUpdate);
        }
        queueCallback();
      });
    });
  };

  /**
   * Check stock
   * @param productId
   * @param {Function|undefined} callback
   */
  const stockCheck = (productId, callback = undefined) => {
    const data = JSON.stringify({
      query: `
        query product($productId: ID!){
            product( id: $productId ) {
              totalInventory
              availableForSale
              variants(first: 6){
                edges{
                  node{
                    availableForSale
                    currentlyNotInStock
                  }
                }
              }
           }
           userErrors {
            field
            message
          }
        }
      `,
      variables: {
        productId
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        callback(response.data.data.product);
      }
    });
  };

  /**
   * Add gift message to the cart
   * @param message
   * @param {Function|undefined} callback
   */
  const addGiftMessageToCart = (message, callback = undefined) => {
    if (!cartId) {
      initCart(() => {
        addGiftMessageToCart(message, callback);
      });
      return;
    }

    const data = JSON.stringify({
      query: `mutation cartAttributesUpdate($cartId: ID! $attributes: [AttributeInput!]!) {
                cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
                  cart {
                    id
                  }
                  userErrors {
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        attributes: {
          key: cartContent.lineItemsAttributes.gift_message,
          value: message
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response);
        }

        queueCallback();
      });
    });
  };

  /**
   * Update country code
   * @param {Function|undefined} callback
   */
  updateCountryCode = (callback = undefined) => {
    if (!cartId) {
      initCart(() => {
        updateCountryCode(callback);
      });
      return;
    }

    const data = JSON.stringify({
      query: `mutation cartBuyerIdentityUpdate($cartId: ID! $buyerIdentity: CartBuyerIdentityInput!) {
                cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
                  cart {
                    id
                  }
                  userErrors {
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        buyerIdentity: {
          countryCode
        }
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  };

  /**
   * Get engraving product
   * @param productId
   * @param {Function|undefined} callback
   */
  const getEngravingProduct = (productId, callback = undefined) => {
    if (!productId) {
      return;
    }

    // @todo Cache engraved product to avoid unnecessary queries

    const data = JSON.stringify({
      query: `
        query product($countryCode: CountryCode!, $productId: ID!)
          @inContext(country: $countryCode) {
            product( id: $productId ) {
              product_id: id
              id: variants(first: 1) {
                edges {
                  node {
                    id
                  }
                }
              }
              title
              availableForSale
              price: variants(first: 1) {
                edges {
                  node {
                    id
                    priceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }`,
      variables: {
        countryCode,
        productId
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        callback(response.data.data.product);
      }
    });
  };

  /**
   * Adds product and engraving product to cart in one click.
   * @param variantId
   * @param engravingId
   * @param message
   * @param productId
   * @param {Function|undefined} callback
   */
  const addToCartWithEngraving = (
    variantId,
    engravingId,
    message,
    productId,
    callback = undefined
  ) => {
    if (!variantId) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.addToCartWithEngraving failed: invalid variant ID',
        {
          variantId
        }
      );
      return;
    }

    if (!engravingId) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.addToCartWithEngraving failed: invalid engraving ID',
        {
          engravingId
        }
      );
      return;
    }

    productId = normaliseProductId(productId);

    if (!cartId) {
      initCart(() => {
        addToCartWithEngraving(
          variantId,
          engravingId,
          message,
          productId,
          callback
        );
      });
      return;
    }

    const linkId = Math.random().toString(36).substring(2);

    const data = JSON.stringify({
      query: `mutation cartLinesAdd($lines: [CartLineInput!]!, $cartId: ID!) {
                cartLinesAdd( lines: $lines, cartId: $cartId ) {
                  cart {
                    id
                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          merchandise {
                            ... on ProductVariant {
                              thumbnail: image {
                                url(transform: { maxHeight: 120, maxWidth: 120 })
                              }
                              product {
                                id
                                title
                                requires_personalisation: metafield(namespace: "custom", key: "requires_personalisation") {
                                  value
                                }
                                personalisation_limit: metafield(namespace: "my_fields", key: "personalisation_limit") {
                                  value
                                }
                                personalisation_product: metafield(namespace: "personalisation", key: "product") {
                                  value
                                }
                                bottled_by_for: metafield(namespace: "custom", key: "bottled_by_for") {
                                  value
                                }
                                bottled_by_only: metafield(namespace: "custom", key: "bottled_by_only") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        lines: [
          {
            merchandiseId: variantId,
            quantity: 1,
            attributes: [
              {
                key: cartContent.lineItemsAttributes.personalisation,
                value: message
              },
              {
                key: cartContent.lineItemsAttributes.product_link,
                value: linkId
              }
            ]
          },
          {
            merchandiseId: engravingId,
            quantity: 1,
            attributes: [
              {
                key: cartContent.lineItemsAttributes.personalisation,
                value: message
              },
              {
                key: cartContent.lineItemsAttributes.product_link,
                value: linkId
              },
              {
                key: cartContent.lineItemsAttributes.is_engrave_product,
                value: 'true'
              }
            ]
          }
        ]
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        events.addToCart(variantId, 1, {
          engraved: true,
          hasCart: hasCartItems()
        });

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }

        queueCallback();
        checkForValidOffers(productId);
      });
    });
  };

  /**
   * Update quantity for multiple IDS
   * @param linkedIds
   * @param quantity
   * @param {Function|undefined} callback
   */
  const multiUpdateQuantity = (linkedIds, quantity, callback) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.multiUpdateQuantity', {
        linkedIds,
        quantity
      });
    }

    if (!linkedIds) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.multiUpdateQuantity failed: invalid linkedI IDs',
        {
          linkedIds
        }
      );
      return;
    }

    if (!cartId) {
      initCart(() => {
        multiUpdateQuantity(linkedIds, quantity, callback);
      });
      return;
    }

    const removeThis = [];

    // eslint-disable-next-line array-callback-return
    linkedIds.map((item) => {
      removeThis.push({
        id: item,
        quantity
      });
    });

    const data = JSON.stringify({
      query: `mutation cartLinesUpdate($cartId: ID! $lines: [CartLineUpdateInput!]!, ) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
                lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          merchandise {
                            ... on ProductVariant {
                              thumbnail: image {
                                url(transform: { maxHeight: 120, maxWidth: 120 })
                              }
                              product {
                                id
                                title
                                requires_personalisation: metafield(namespace: "custom", key: "requires_personalisation") {
                                  value
                                }
                                personalisation_limit: metafield(namespace: "my_fields", key: "personalisation_limit") {
                                  value
                                }
                                personalisation_product: metafield(namespace: "personalisation", key: "product") {
                                  value
                                }
                                bottled_by_for: metafield(namespace: "custom", key: "bottled_by_for") {
                                  value
                                }
                                bottled_by_only: metafield(namespace: "custom", key: "bottled_by_only") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
              }
              userErrors {
                code
                field
                message
              }
            }
          }`,
      variables: {
        cartId,
        lines: removeThis
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }
        queueCallback();
      });
    });
  };

  /**
   * Add promo code to cart
   * @param promocode
   * @param {Function|undefined} callback
   */
  const addPromoCodeToCart = (promocode, callback = undefined) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.addPromoCodeToCart', {
        promocode
      });
    }

    if (!cartId) {
      initCart(() => {
        addPromoCodeToCart(promocode, callback);
      });
    }

    const data = JSON.stringify({
      query: `mutation cartDiscountCodesUpdate($cartId: ID! $discountCodes: [String!]) {
                cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                  cart {
                    id
                  }
                  userErrors {
                    field
                    message
                  }

                }
              }`,
      variables: {
        cartId,
        discountCodes: [promocode]
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.log(
          '[DEBUG] cartService.addPromoCodeToCart, added discount code ',
          response
        );
      }
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  };

  /**
   * Update country code
   * @param newCountryCode
   * @param {Function|undefined} callback
   */
  updateCountryCodeWith = (newCountryCode, callback = undefined) => {
    if (!cartId) {
      initCart(() => {
        updateCountryCodeWith(newCountryCode, callback);
      });
      return;
    }

    let changeCountryCode;
    if (newCountryCode.length === 2) {
      const marketLocale = markets.getCountryLocale(newCountryCode);
      changeCountryCode = markets.localeToCountryCode(marketLocale, 'IE');
      if (marketLocale) {
        countryCode = newCountryCode;
        globalTheme.setCurrentMarket(marketLocale);
      }
    } else {
      changeCountryCode = markets.localeToCountryCode(
        newCountryCode.locale,
        'IE'
      );
    }

    const data = JSON.stringify({
      query: `mutation cartBuyerIdentityUpdate($cartId: ID! $buyerIdentity: CartBuyerIdentityInput!) {
                cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
                  cart {
                    id
                    buyerIdentity{
                      countryCode
                    }
                    cost {
                      totalTaxAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                  userErrors {
                    field
                    message
                  }

                }
              }`,
      variables: {
        cartId,
        buyerIdentity: { countryCode: changeCountryCode }
      }
    });

    const config = getHttpConfig(data);

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  };

  async function checkStock(productId, quantity) {
    if (!productId) {
      return false;
    }

    const data = JSON.stringify({
      query: `query Product($productId: ID!){
                  product(id: $productId) {
                    totalInventory
                    availableForSale
                }}`,
      variables: { productId }
    });

    const config = getHttpConfig(data);
    try {
      const response = await http(config);

      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
        return false;
      }

      const product = response.data?.data?.product;
      if (!product) {
        return false;
      }

      const { availableForSale, totalInventory } = product;

      if(availableForSale){
        // Early return for untracked, dont care about stock level.
        return true;
      }

      if (typeof totalInventory !== 'number') {
        return Boolean(availableForSale);
      }

      return availableForSale && totalInventory >= quantity;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Stock check failed:', error);
      return false;
    }
  }

  /**
   * @param {Array} recipients
   * @param product
   * @param quantity
   * @param {Boolean} engraving If true, the product is engraving bottle
   * @param {Boolean} giftBag If true, the product is gift bag - DEPRECATED
   * @param {String} linkedVariantId Mandatory, if engraving or gift bag is set to true
   * @param {Function|undefined} callback
   */
  const addRecipientsWithProductToCart = async (
    recipients,
    product,
    quantity,
    engraving = false,
    giftBag = false,
    linkedVariantId = '',
    callback = undefined
  ) => {
    const lines = [];

    if (recipients.length > 50) {
      window.dispatchEvent(
        new CustomEvent('error-toggled', {
          detail: {
            subtitle: 'Error',
            title: 'An error occurred',
            content: 'Please add a maximum of 50 contacts at a time.'
          }
        })
      );
      return;
    }

    const resolvedProduct = await globalTheme.productByVariantId(product);
    const hasStock = await checkStock(resolvedProduct.id, recipients.length);

    if (!hasStock) {
      // eslint-disable-next-line no-console
      console.error('Not enough stock', resolvedProduct.id, recipients.length);
      window.dispatchEvent(
        new CustomEvent('error-toggled', {
          detail: {
            subtitle: 'Error',
            title: 'An error occurred adding this item to the cart',
            content:
              'There is not enough stock available for this number of order items.'
          }
        })
      );
      return;
    }

    recipients.forEach((recipient) => {
      if (!recipient) {
        return;
      }

      const linkId = Math.random().toString(36).substring(2);

      const attributesMain = [
        {
          key: cartContent.lineItemsAttributes.b2b_recipient,
          value: recipient
        }
      ];

      if (engraving || giftBag) {
        const attributesEngravingOrGiftBag = [
          {
            key: cartContent.lineItemsAttributes.b2b_recipient,
            value: recipient
          },
          {
            key: cartContent.lineItemsAttributes.product_link,
            value: linkId
          },
          {
            key: cartContent.lineItemsAttributes.b2b_linked_variant_id,
            value: linkedVariantId
          }
        ];

        if (engraving) {
          attributesMain.push({
            key: cartContent.lineItemsAttributes.is_engrave_product,
            value: 'true'
          });
          attributesEngravingOrGiftBag.push({
            key: cartContent.lineItemsAttributes.is_engrave_product,
            value: 'true'
          });
        }

        if (giftBag) {
          attributesMain.push({
            key: cartContent.lineItemsAttributes.gift_bag,
            value: 'true'
          });
          attributesEngravingOrGiftBag.push({
            key: cartContent.lineItemsAttributes.gift_bag,
            value: 'true'
          });
        }

        lines.push({
          quantity,
          merchandiseId: product,
          attributes: attributesEngravingOrGiftBag
        });
        attributesMain.push({
          key: cartContent.lineItemsAttributes.product_link,
          value: linkId
        });
      }

      lines.push({
        quantity,
        merchandiseId: product,
        attributes: attributesMain
      });
    });

    const data = JSON.stringify({
      query: `mutation cartLinesAdd($cartId: ID! $lines: [CartLineInput!]!) {
                cartLinesAdd(cartId: $cartId, lines: $lines) {
                  cart {
                    id
                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          attributes {
                            key
                            value
                          }
                          merchandise {
                            ... on ProductVariant {
                              thumbnail: image {
                                url(transform: { maxHeight: 120, maxWidth: 120 })
                              }
                              id
                              weight
                              product {
                                id
                                title
                                requires_personalisation: metafield(namespace: "custom", key: "requires_personalisation") {
                                  value
                                }
                                personalisation_limit: metafield(namespace: "my_fields", key: "personalisation_limit") {
                                  value
                                }
                                personalisation_product: metafield(namespace: "personalisation", key: "product") {
                                  value
                                }
                                bottled_by_for: metafield(namespace: "custom", key: "bottled_by_for") {
                                  value
                                }
                                bottled_by_only: metafield(namespace: "custom", key: "bottled_by_only") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                }
              }`,
      variables: {
        cartId,
        lines
      }
    });

    const config = getHttpConfig(data);
    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      response.sentQuantity = quantity;
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  };

  /**
   * Update B2B cart line item with personalisation
   * @param {String} cartLineId
   * @param {Array} cartLineAttributes
   * @param {String} label
   * @param {Function|undefined} callback
   */
  const updateB2BCartWithPersonalisation = (
    cartLineId,
    cartLineAttributes,
    label,
    callback = undefined
  ) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.updateB2BCartWithPersonalisation', {
        cartLineId,
        cartLineAttributes,
        label
      });
    }

    if (!cartLineId) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.updateB2BCartWithPersonalisation failed: invalid cart line ID',
        {
          cartLineId
        }
      );
      return;
    }

    if (!cartId) {
      initCart(() => {
        updateB2BCartWithPersonalisation(
          cartLineId,
          cartLineAttributes,
          label,
          callback
        );
      });
      return;
    }

    if (cartLineAttributes && isArray(cartLineAttributes)) {
      cartLineAttributes.push({
        key: cartContent.lineItemsAttributes.personalisation,
        value: label
      });
      cartLineAttributes.push({
        key: cartContent.lineItemsAttributes.b2b_is_linked_product,
        value: 'true'
      });
    }

    const data = JSON.stringify({
      query: `mutation cartLinesUpdate($cartId: ID! $lines: [CartLineUpdateInput!]!, ) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
              }
              userErrors {
                code
                field
                message
              }
            }
          }`,
      variables: {
        cartId,
        lines: {
          id: cartLineId,
          attributes: cartLineAttributes
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }
        queueCallback();
      });
    });
  };

  /**
   * Update B2B cart line item with bottled by
   * @param {String} cartLineId
   * @param {Array} cartLineAttributes
   * @param {Object} message
   * @param {Function|undefined} callback
   * @param {Boolean} withFor Default true, it will handle "for" input. If bottled by only (without for), set it to false
   */
  const updateB2BCartWithBottledBy = (
    cartLineId,
    cartLineAttributes,
    message,
    callback = undefined,
    withFor = true
  ) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] cartService.updateB2BCartWithBottledBy', {
        cartLineId,
        cartLineAttributes,
        message,
        withFor
      });
    }

    if (!cartLineId) {
      // eslint-disable-next-line no-console
      console.error(
        'cartService.updateB2BCartWithBottledBy failed: invalid cart line ID',
        {
          cartLineId
        }
      );
      return;
    }

    if (!cartId) {
      initCart(() => {
        updateB2BCartWithBottledBy(
          cartLineId,
          cartLineAttributes,
          message,
          callback,
          withFor
        );
      });
      return;
    }

    if (cartLineAttributes && isArray(cartLineAttributes)) {
      cartLineAttributes.push({
        key: cartContent.lineItemsAttributes.bottled_by,
        value: message.bottledBy
      });
      if (withFor) {
        cartLineAttributes.push({
          key: cartContent.lineItemsAttributes.for,
          value: message.for
        });
      }
    }

    const data = JSON.stringify({
      query: `mutation cartLinesUpdate($cartId: ID! $lines: [CartLineUpdateInput!]!, ) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
              }
              userErrors {
                code
                field
                message
              }
            }
          }`,
      variables: {
        cartId,
        lines: {
          id: cartLineId,
          attributes: cartLineAttributes
        }
      }
    });

    const config = getHttpConfig(data);

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          updateCountryCode();
          callback(response);
        }
        queueCallback();
      });
    });
  };

  /**
   * Update B2B cart line item with bottled by
   * @param {String} cartLineId
   * @param {Array} cartLineAttributes
   * @param {Object} message
   * @param {Function|undefined} callback
   */
  const updateB2BCartWithBottledByOnly = (
    cartLineId,
    cartLineAttributes,
    message,
    callback = undefined
  ) => {
    updateB2BCartWithBottledBy(
      cartLineId,
      cartLineAttributes,
      message,
      callback,
      false
    );
  };

  /**
   * Check if the specified product variantId is already at max capacity in the cart
   * @param {object} product
   * @returns Boolean
   */
  const doesCartHaveMaxQuantityOfProduct = (product) => {
    if (
      !product ||
      typeof product !== 'object' ||
      typeof cartLines !== 'object' ||
      cartLines.length === 0
    ) {
      return false;
    }

    const meta = product?.shopify_metafields ?? product?.meta ?? {};
    const purchaseLimit = meta?.purchase_limit ?? false;
    if (!purchaseLimit) {
      return false;
    }

    const quantityInCart = findCartQuantityTotalByProduct(product);
    return quantityInCart >= purchaseLimit;
  };

  return {
    uniqueCartId,
    getCart,
    getCartId: () => cartId,
    addToCart,
    stockCheck,
    updateCart,
    grabGiftBag,
    addUserToCart,
    addToCartWithBottledBy,
    addToCartWithBottledByOnly,
    updateQuantity,
    removeFromCart,
    updateCountryCode,
    multiUpdateQuantity,
    getEngravingProduct,
    checkForValidOffers,
    addGiftMessageToCart,
    updateCountryCodeWith,
    addToCartWithEngraving,
    updateCartItemsCounter,
    togglePersonalisationModel,
    toggleVariantPersonalisationModel,
    addPromoCodeToCart,
    addToCartWithPersonalisation,
    addRecipientsWithProductToCart,
    updateB2BCartWithPersonalisation,
    updateB2BCartWithBottledBy,
    updateB2BCartWithBottledByOnly,
    doesCartHaveMaxQuantityOfProduct,
    findCartQuantityTotalByProduct,
    events: () => events
  };
}
// eslint-disable-next-line func-names
const cartService = (function () {
  let instance = null;

  return {
    getInstance: () => {
      if (instance === null) {
        instance = new CartServiceConstructor();
        if (debugEnabled) {
          // eslint-disable-next-line no-console
          console.log('*** new CartService instance', instance.uniqueCartId);
        }
      }
      return instance;
    }
  };
})();

export default cartService;
